<template>
    <div class="score-evolution-wrap" :class="{ 'score-evolution-broadcast': broadcast }">
        <div class="score-evolution-shell">
            <!-- Summary tiles above the chart (bigger on the live stream) -->
            <div v-if="chartSeries.derivedFrom !== 'empty'" key="flow-summary" class="flow-summary">
                <div class="flow-tile" :style="{ borderTopColor: team1LineColor }">
                    <span class="flow-tile__team">
                        <img v-if="team1 && team1.logo" :src="`${assetsUrl}/${team1.logo}`" alt="">
                        {{ team1 && team1.name ? team1.name : 'Ομάδα 1' }}
                    </span>
                    <span class="flow-tile__value">+{{ leadSummary.team1LargestLead }}</span>
                    <span class="flow-tile__label">Μεγαλύτερη διαφορά</span>
                </div>
                <div class="flow-tile" :style="{ borderTopColor: team2LineColor }">
                    <span class="flow-tile__team">
                        <img v-if="team2 && team2.logo" :src="`${assetsUrl}/${team2.logo}`" alt="">
                        {{ team2 && team2.name ? team2.name : 'Ομάδα 2' }}
                    </span>
                    <span class="flow-tile__value">+{{ leadSummary.team2LargestLead }}</span>
                    <span class="flow-tile__label">Μεγαλύτερη διαφορά</span>
                </div>
                <div class="flow-tile">
                    <span class="flow-tile__value">{{ leadSummary.leadChanges }}</span>
                    <span class="flow-tile__label">Αλλαγές προβαδίσματος</span>
                </div>
                <div class="flow-tile">
                    <span class="flow-tile__value">{{ leadSummary.ties }}</span>
                    <span class="flow-tile__label">Ισοπαλίες</span>
                </div>
                <div v-if="biggestRun" class="flow-tile flow-tile--run" :style="{ borderTopColor: biggestRun.color }">
                    <span class="flow-tile__team">
                        <img v-if="biggestRun.team && biggestRun.team.logo" :src="`${assetsUrl}/${biggestRun.team.logo}`" alt="">
                        {{ biggestRun.period }}
                    </span>
                    <span class="flow-tile__value">{{ biggestRun.points }}-{{ biggestRun.against }}</span>
                    <span class="flow-tile__label">Μεγαλύτερο σερί</span>
                </div>
            </div>

            <div v-if="!broadcast" key="legend" class="score-legend">
                <span class="legend-item">
                    <span class="legend-line" :style="{ backgroundColor: team1LineColor }" />
                    {{ team1 && team1.name ? team1.name : 'Ομάδα 1' }}
                </span>
                <span class="legend-item">
                    <span class="legend-line" :style="{ backgroundColor: team2LineColor }" />
                    {{ team2 && team2.name ? team2.name : 'Ομάδα 2' }}
                </span>
            </div>

            <!-- keyed so Vue keeps this canvas (the one Chart.js draws on) when the blocks around it appear -->
            <div key="chart" class="score-evolution-chart">
                <canvas ref="canvas" />
            </div>

            <!-- Score difference minute by minute: above the line team 1 leads, below team 2 -->
            <div v-if="!broadcast && chartSeries.derivedFrom !== 'empty'" key="flow-diff" class="flow-diff">
                <div class="flow-diff__head">
                    <span class="flow-diff__title">Διαφορά σκορ</span>
                    <span class="flow-diff__legend">
                        <span class="legend-line" :style="{ backgroundColor: team1LineColor }" /> {{ team1 && team1.name }} μπροστά
                        <span class="legend-line ml-3" :style="{ backgroundColor: team2LineColor }" /> {{ team2 && team2.name }} μπροστά
                    </span>
                </div>
                <div class="flow-diff__plot" :style="{ paddingLeft: `${plotInset.left}px`, paddingRight: `${plotInset.right}px` }">
                    <span class="flow-diff__max flow-diff__max--top" :style="{ left: `${plotInset.left - 36}px` }">+{{ diffSeries.max }}</span>
                    <span class="flow-diff__max flow-diff__max--bottom" :style="{ left: `${plotInset.left - 36}px` }">−{{ diffSeries.max }}</span>
                    <svg class="flow-diff__svg" :viewBox="`0 0 ${diffSeries.width} 100`" preserveAspectRatio="none">
                        <line x1="0" :x2="diffSeries.width" y1="50" y2="50" class="flow-diff__axis" />
                        <line
                            v-for="marker in diffSeries.markers"
                            :key="`m-${marker}`"
                            :x1="marker"
                            :x2="marker"
                            y1="0"
                            y2="100"
                            class="flow-diff__marker"
                        />
                        <rect
                            v-for="bar in diffSeries.bars"
                            :key="bar.minute"
                            :x="bar.x"
                            :y="bar.y"
                            :width="bar.width"
                            :height="bar.height"
                            :fill="bar.diff > 0 ? team1LineColor : team2LineColor"
                        />
                    </svg>
                    <!-- the minute of each bar on hover / on a tap: a column over the whole height of it -->
                    <div class="flow-diff__hits" :style="{ left: `${plotInset.left}px`, right: `${plotInset.right}px` }">
                        <info-tip
                            v-for="bar in diffSeries.bars"
                            :key="`hit-${bar.minute}`"
                            :activator-style="{ position: 'absolute', top: 0, left: `${(bar.x / diffSeries.width) * 100}%`, width: `${(bar.width / diffSeries.width) * 100}%` }"
                            :text="`${bar.minute}ο λεπτό: ${bar.diff > 0 ? '+' : ''}${bar.diff}`"
                            block
                        />
                    </div>
                    <div class="flow-diff__periods">
                        <span v-for="segment in diffSeries.segments" :key="segment.label" :style="{ flexGrow: segment.end - segment.start }">
                            {{ segment.label }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="chartSeries.derivedFrom === 'empty'" class="chart-note">
            Δεν υπάρχουν ακόμα live δεδομένα για αυτόν τον αγώνα.
        </div>
    </div>
</template>

<script>
    import { Line } from 'vue-chartjs'
    import { teamColorsOn } from '@/utils/colors'

    export default {
        name: 'ScoreEvolutionChart',
        extends: Line,
        props: {
            logs: {
                type: Array,
                default: () => []
            },
            team1: {
                type: Object,
                default: () => ({})
            },
            team2: {
                type: Object,
                default: () => ({})
            },
            // TV/broadcast styling: bigger text, thicker lines, no tooltips or point markers
            broadcast: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                assetsUrl: 'https://storage.googleapis.com',
                // left / right margins of the line chart's plot area, so the difference strip lines up with its minutes
                plotInset: { left: 34, right: 0 },
                chartOptions: {
                    responsive: true,
                    maintainAspectRatio: false,
                    layout: {
                        padding: {
                            top: 26,
                            right: 10,
                            left: 8,
                            bottom: 0
                        }
                    },
                    elements: {
                        line: {
                            tension: 0,
                            borderWidth: 3
                        },
                        point: {
                            radius: 2,
                            hoverRadius: 4
                        }
                    },
                    legend: {
                        display: false
                    },
                    scales: {
                        xAxes: [{
                            scaleLabel: {
                                display: true,
                                labelString: 'Λεπτό',
                                fontColor: '#2f3f53',
                                fontStyle: 'bold'
                            },
                            ticks: {
                                autoSkip: false,
                                fontColor: '#415269',
                                callback: (value) => {
                                    const minute = Number(value)
                                    if (!Number.isFinite(minute)) return ''
                                    return minute % 10 === 0 ? `${minute}` : ''
                                }
                            },
                            gridLines: {
                                color: 'rgba(0, 0, 0, 0.08)',
                                zeroLineColor: 'rgba(0, 0, 0, 0.20)'
                            }
                        }],
                        yAxes: [{
                            scaleLabel: {
                                display: true,
                                labelString: 'Πόντοι',
                                fontColor: '#2f3f53',
                                fontStyle: 'bold'
                            },
                            ticks: {
                                beginAtZero: true,
                                precision: 0,
                                fontColor: '#415269'
                            },
                            gridLines: {
                                color: 'rgba(0, 0, 0, 0.08)',
                                zeroLineColor: 'rgba(0, 0, 0, 0.20)'
                            }
                        }]
                    },
                    tooltips: {
                        backgroundColor: '#ffffff',
                        titleFontColor: '#111827',
                        bodyFontColor: '#111827',
                        borderColor: '#d1d5db',
                        borderWidth: 1,
                        mode: 'index',
                        intersect: false,
                        callbacks: {
                            title: (tooltipItems) => {
                                if (!tooltipItems || !tooltipItems.length) return ''
                                return `${tooltipItems[0].label}ο λεπτό`
                            },
                            label: (tooltipItem, data) => {
                                const datasetLabel = data.datasets[tooltipItem.datasetIndex].label || ''
                                return `${datasetLabel}: ${tooltipItem.yLabel}`
                            },
                            afterBody: (tooltipItems) => {
                                if (!tooltipItems || !tooltipItems.length) return []
                                const minute = Number(tooltipItems[0].label)
                                const diffLine = this.diffText(minute)
                                const events = (this.chartSeries.minuteEvents[minute] || []).filter(
                                    line => {
                                        const text = String(line || '').trim()
                                        if (!text) return false
                                        return !/^\+\d+$/.test(text)
                                    }
                                )
                                return diffLine ? [diffLine, ...events] : events
                            }
                        }
                    },
                    hover: {
                        mode: 'nearest',
                        intersect: false
                    }
                }
            }
        },
        computed: {
            activeOptions() {
                if (!this.broadcast) {
                    if (!this.$vuetify.breakpoint.xs) return this.chartOptions
                    // phone: no axis titles (they eat the narrow width) and straight minute labels
                    const options = this.chartOptions
                    const x = options.scales.xAxes[0]
                    const y = options.scales.yAxes[0]
                    return {
                        ...options,
                        layout: { padding: { top: 22, right: 6, left: 0, bottom: 0 } },
                        scales: {
                            xAxes: [{ ...x, scaleLabel: { ...x.scaleLabel, display: false }, ticks: { ...x.ticks, maxRotation: 0, minRotation: 0 } }],
                            yAxes: [{ ...y, scaleLabel: { ...y.scaleLabel, display: false } }]
                        }
                    }
                }
                const options = this.chartOptions
                const withBigFonts = axis => ({
                    ...axis,
                    scaleLabel: { ...axis.scaleLabel, fontSize: 20 },
                    ticks: { ...axis.ticks, fontSize: 18, fontStyle: 'bold' }
                })
                return {
                    ...options,
                    layout: { padding: { top: 40, right: 16, left: 8, bottom: 0 } },
                    elements: {
                        line: { ...options.elements.line, borderWidth: 5 },
                        point: { radius: 0, hoverRadius: 0 }
                    },
                    scales: {
                        xAxes: [withBigFonts(options.scales.xAxes[0])],
                        yAxes: [withBigFonts(options.scales.yAxes[0])]
                    },
                    tooltips: { enabled: false },
                    hover: { mode: null }
                }
            },
            // visible on the white chart and different from each other
            lineColors() {
                return teamColorsOn(this.normalizeColor(this.team1?.color) || '#f57c2f', this.normalizeColor(this.team2?.color) || '#74b8f1')
            },
            team1LineColor() {
                return this.lineColors.team1
            },
            team2LineColor() {
                return this.lineColors.team2
            },
            chartSeries() {
                return this.buildSeries()
            },
            leadSummary() {
                const team1Series = this.chartSeries?.team1Series || []
                const team2Series = this.chartSeries?.team2Series || []

                let team1LargestLead = 0
                let team2LargestLead = 0
                let leadChanges = 0
                let previousLeader = 0
                let ties = 0
                let previousDiff = 0

                for (let i = 0; i < Math.min(team1Series.length, team2Series.length); i++) {
                    const score1 = Number(team1Series[i]) || 0
                    const score2 = Number(team2Series[i]) || 0
                    const diff = score1 - score2

                    if (diff > team1LargestLead) team1LargestLead = diff
                    if (-diff > team2LargestLead) team2LargestLead = -diff

                    // a tie counts when the scores meet again after someone led (0-0 at tip-off is not one)
                    if (diff === 0 && previousDiff !== 0) ties += 1
                    previousDiff = diff

                    const currentLeader = diff > 0 ? 1 : (diff < 0 ? 2 : 0)
                    if (currentLeader !== 0) {
                        if (previousLeader !== 0 && currentLeader !== previousLeader) {
                            leadChanges += 1
                        }
                        previousLeader = currentLeader
                    }
                }

                return {
                    team1LargestLead,
                    team2LargestLead,
                    leadChanges,
                    ties
                }
            },
            // Longest stretch where one team kept scoring and the other did not (minute by minute, from the logs)
            biggestRun() {
                const team1Series = this.chartSeries?.team1Series || []
                const team2Series = this.chartSeries?.team2Series || []
                let best = null
                let current = null
                let previous1 = 0
                let previous2 = 0

                for (let minute = 0; minute < Math.min(team1Series.length, team2Series.length); minute++) {
                    if (team1Series[minute] === null || team2Series[minute] === null) break
                    const delta1 = (Number(team1Series[minute]) || 0) - previous1
                    const delta2 = (Number(team2Series[minute]) || 0) - previous2
                    previous1 = Number(team1Series[minute]) || 0
                    previous2 = Number(team2Series[minute]) || 0

                    const scorer = delta1 > 0 && delta2 === 0 ? 1 : (delta2 > 0 && delta1 === 0 ? 2 : (delta1 > 0 || delta2 > 0 ? -1 : 0))
                    if (scorer === 0) continue
                    if (scorer === -1) {
                        current = null
                        continue
                    }
                    if (current && current.team === scorer) {
                        current.points += scorer === 1 ? delta1 : delta2
                    } else {
                        current = { team: scorer, points: scorer === 1 ? delta1 : delta2, startMinute: minute }
                    }
                    if (!best || current.points > best.points) {
                        best = { ...current }
                    }
                }

                if (!best || best.points < 6) return null
                return {
                    points: best.points,
                    against: 0,
                    team: best.team === 1 ? this.team1 : this.team2,
                    color: best.team === 1 ? this.team1LineColor : this.team2LineColor,
                    period: this.periodName(best.startMinute)
                }
            },
            diffSeries() {
                const team1Series = this.chartSeries?.team1Series || []
                const team2Series = this.chartSeries?.team2Series || []
                const maxMinute = Math.max(1, this.chartSeries?.maxMinute || 40)
                const width = 1000
                // like the line chart: minute 0 on the left edge, the last minute on the right edge
                const step = width / maxMinute
                const diffs = []
                for (let minute = 0; minute <= maxMinute; minute++) {
                    if (team1Series[minute] === null || team1Series[minute] === undefined) break
                    diffs.push((Number(team1Series[minute]) || 0) - (Number(team2Series[minute]) || 0))
                }
                const max = Math.max(5, ...diffs.map(Math.abs))
                const bars = diffs.map((diff, minute) => {
                    const height = Math.abs(diff) / max * 48
                    return {
                        minute,
                        diff,
                        x: minute * step - step * 0.38,
                        width: step * 0.76,
                        y: diff > 0 ? 50 - height : 50,
                        height
                    }
                }).filter(bar => bar.diff !== 0)
                const segments = this.getPeriodSegments(maxMinute).map(segment => ({ ...segment, label: segment.label }))
                const markers = segments.slice(0, -1).map(segment => segment.end * step)
                return { width, bars, max, segments, markers }
            },
            chartData() {
                const { labels, team1Series, team2Series } = this.chartSeries
                return {
                    labels,
                    datasets: [
                        {
                            label: this.team1?.name || 'Team 1',
                            data: team1Series,
                            borderColor: this.team1LineColor,
                            backgroundColor: this.team1LineColor,
                            pointBackgroundColor: this.team1LineColor,
                            pointBorderColor: this.team1LineColor,
                            fill: false
                        },
                        {
                            label: this.team2?.name || 'Team 2',
                            data: team2Series,
                            borderColor: this.team2LineColor,
                            backgroundColor: this.team2LineColor,
                            pointBackgroundColor: this.team2LineColor,
                            pointBorderColor: this.team2LineColor,
                            fill: false
                        }
                    ]
                }
            }
        },
        watch: {
            chartData: {
                handler(nextChartData) {
                    this.renderChart(nextChartData, this.activeOptions)
                },
                deep: true
            }
        },
        mounted() {
            this.addPlugin({
                afterDraw: (chart) => {
                    const xAxis = chart.scales['x-axis-0']
                    const yAxis = chart.scales['y-axis-0']
                    if (!xAxis || !yAxis) return

                    this.syncPlotInset(chart)
                    const segments = this.getPeriodSegments(this.chartSeries.maxMinute)
                    const ctx = chart.chart.ctx
                    ctx.save()

                    segments.forEach((segment, index) => {
                        const startX = xAxis.getPixelForValue(segment.start)
                        const endX = xAxis.getPixelForValue(segment.end)
                        if (index % 2 === 1) {
                            ctx.fillStyle = 'rgba(0, 0, 0, 0.03)'
                            ctx.fillRect(startX, yAxis.top, endX - startX, yAxis.bottom - yAxis.top)
                        }
                    })

                    ctx.strokeStyle = 'rgba(0, 0, 0, 0.30)'
                    ctx.lineWidth = 1.3
                    segments.forEach(segment => {
                        const x = xAxis.getPixelForValue(segment.end)
                        ctx.beginPath()
                        ctx.moveTo(x, yAxis.top)
                        ctx.lineTo(x, yAxis.bottom)
                        ctx.stroke()
                    })

                    ctx.fillStyle = '#4f5f74'
                    ctx.font = this.broadcast ? 'bold 20px sans-serif' : 'bold 12px sans-serif'
                    ctx.textAlign = 'center'
                    segments.forEach(segment => {
                        const midpoint = (segment.start + segment.end) / 2
                        const x = xAxis.getPixelForValue(midpoint)
                        if (segment.end - segment.start >= 3) {
                            ctx.fillText(segment.label, x, yAxis.top - (this.broadcast ? 12 : 8))
                        }
                    })

                    ctx.restore()
                }
            })

            this.renderChart(this.chartData, this.activeOptions)
        },
        methods: {
            syncPlotInset(chart) {
                const area = chart.chartArea
                if (!area) return
                // + the 4px padding of the chart box
                const left = Math.round(area.left) + 4
                const right = Math.round(chart.width - area.right) + 4
                if (Math.abs(left - this.plotInset.left) > 1 || Math.abs(right - this.plotInset.right) > 1) {
                    this.plotInset = { left, right }
                }
            },
            periodName(minute) {
                if (minute < 40) return `${Math.floor(minute / 10) + 1}η περίοδος`
                return `Παράταση ${Math.floor((minute - 40) / 5) + 1}`
            },
            diffText(minute) {
                const score1 = Number(this.chartSeries.team1Series[minute])
                const score2 = Number(this.chartSeries.team2Series[minute])
                if (!Number.isFinite(score1) || !Number.isFinite(score2)) return ''
                const diff = score1 - score2
                if (diff === 0) return 'Ισοπαλία'
                const leader = diff > 0 ? this.team1 : this.team2
                return `Διαφορά: +${Math.abs(diff)} ${leader && leader.name ? leader.name : ''}`
            },
            normalizeColor(value) {
                if (!value || typeof value !== 'string') return null
                const trimmed = value.trim()
                const cssColorRegex = /^(#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})|rgb\(|rgba\(|hsl\(|hsla\()/
                return cssColorRegex.test(trimmed) ? trimmed : null
            },
            normalizeLogsPayload(logs) {
                if (!logs) return []
                if (Array.isArray(logs)) return logs
                if (Array.isArray(logs.logs)) return logs.logs
                if (Array.isArray(logs.items)) return logs.items
                if (Array.isArray(logs.data)) return logs.data
                if (Array.isArray(logs.content)) return logs.content
                if (Array.isArray(logs.results)) return logs.results
                return []
            },
            toNumber(value) {
                const n = Number(value)
                return Number.isFinite(n) ? n : null
            },
            toCount(value) {
                if (value === true) return 1
                if (value === false || value === null || value === undefined) return 0
                const n = Number(value)
                return Number.isFinite(n) ? Math.max(0, n) : 0
            },
            extractText(log) {
                const parts = [
                    log.description,
                    log.message,
                    log.action,
                    log.eventType,
                    log.type,
                    log.title,
                    log.text
                ].filter(Boolean)
                return parts.join(' ')
            },
            getPeriod(log) {
                return this.toNumber(log.period ?? log.quarter ?? log.q ?? log.currentPeriod)
            },
            getTimestampMs(log) {
                const sources = [log, log.data, log.stats, log.event, log.payload].filter(
                    value => value && typeof value === 'object'
                )

                for (const source of sources) {
                    const raw =
                        source.timestamp ??
                        source.timeStamp ??
                        source.createdAt ??
                        source.eventTimestamp ??
                        source.eventTime ??
                        source.datetime ??
                        source.time ??
                        null

                    if (raw === null || raw === undefined) continue

                    if (typeof raw === 'number') {
                        if (!Number.isFinite(raw)) continue
                        return raw < 1e12 ? raw * 1000 : raw
                    }

                    const asNumber = Number(raw)
                    if (Number.isFinite(asNumber)) {
                        return asNumber < 1e12 ? asNumber * 1000 : asNumber
                    }

                    const parsed = Date.parse(raw)
                    if (Number.isFinite(parsed)) {
                        return parsed
                    }
                }

                return null
            },
            parseClockToMinute(log) {
                const clock = log.clock ?? log.time ?? log.timeLeft ?? log.timer
                if (!clock || typeof clock !== 'string') return null

                const match = clock.match(/(\d{1,2}):(\d{2})/)
                if (!match) return null
                const minutesLeft = this.toNumber(match[1])
                const secondsLeft = this.toNumber(match[2])
                const period = this.getPeriod(log)
                if (minutesLeft === null || secondsLeft === null || period === null) return null

                const elapsedInPeriod = Math.max(0, (10 * 60) - (minutesLeft * 60 + secondsLeft))
                return Math.max(0, Math.floor(((period - 1) * 10) + (elapsedInPeriod / 60)))
            },
            parseMinuteFromTimestampString(log) {
                const sources = [log, log.data, log.stats, log.event, log.payload].filter(
                    value => value && typeof value === 'object'
                )

                for (const source of sources) {
                    const raw =
                        source.timestamp ??
                        source.timeStamp ??
                        source.eventTime ??
                        source.time ??
                        null
                    if (!raw || typeof raw !== 'string') continue

                    const match = raw.trim().match(/^(\d{1,3}):(\d{1,2})(?::\d{1,2})?$/)
                    if (!match) continue

                    const minute = this.toNumber(match[1])
                    if (minute !== null) return Math.max(0, Math.floor(minute))
                }

                return null
            },
            getMinute(log, firstTimestampMs = null) {
                const directMinute = this.toNumber(
                    log.minute ?? log.matchMinute ?? log.gameMinute ?? log.min
                )
                if (directMinute !== null) {
                    return Math.max(0, Math.floor(directMinute))
                }

                const fromTimestampString = this.parseMinuteFromTimestampString(log)
                if (fromTimestampString !== null) {
                    return fromTimestampString
                }

                const fromClock = this.parseClockToMinute(log)
                if (fromClock !== null) {
                    return fromClock
                }

                const elapsedSeconds = this.toNumber(
                    log.elapsedSeconds ?? log.matchSecond ?? log.gameSecond ?? log.second
                )
                if (elapsedSeconds !== null) {
                    return Math.max(0, Math.floor(elapsedSeconds / 60))
                }

                const timestampMs = this.getTimestampMs(log)
                if (timestampMs !== null && firstTimestampMs !== null) {
                    return Math.max(0, Math.floor((timestampMs - firstTimestampMs) / 60000))
                }

                return null
            },
            getTeamId(log) {
                const sources = [log, log.data, log.stats, log.event, log.payload].filter(
                    value => value && typeof value === 'object'
                )
                for (const source of sources) {
                    const id = source.teamId ?? source.team?.id ?? source.team?.teamId ?? null
                    if (id !== null && id !== undefined) return id
                }
                return null
            },
            getTeamName(log) {
                const sources = [log, log.data, log.stats, log.event, log.payload].filter(
                    value => value && typeof value === 'object'
                )
                for (const source of sources) {
                    const name = source.teamName || source.team?.name || ''
                    if (name) return String(name).toLowerCase()
                }
                return ''
            },
            getMadePoints(log) {
                const nested = [log.data, log.stats, log.event, log.payload].find(
                    value => value && typeof value === 'object'
                ) || {}

                const freeThrowsMade = this.toCount(
                    log.freeThrowsMade ?? nested.freeThrowsMade ?? log.freeThrowMade ?? nested.freeThrowMade
                )
                const threePointsMade = this.toCount(
                    log.threePointsMade ?? nested.threePointsMade ?? log.threePointMade ?? nested.threePointMade
                )
                const twoPointsMade = this.toCount(
                    log.twoPointsMade ?? nested.twoPointsMade ?? log.twoPointMade ?? nested.twoPointMade
                )
                const totalMade = freeThrowsMade + threePointsMade + twoPointsMade

                if (totalMade <= 0) return null
                return freeThrowsMade + (threePointsMade * 3) + (twoPointsMade * 2)
            },
            getTeamScoreFromStats(teamStats) {
                if (!teamStats || typeof teamStats !== 'object') return null

                const freeThrowsMade = this.toCount(teamStats.freeThrowsMade ?? teamStats.freeThrowMade)
                const threePointsMade = this.toCount(teamStats.threePointsMade ?? teamStats.threePointMade)
                const twoPointsMade = this.toCount(teamStats.twoPointsMade ?? teamStats.twoPointMade)
                const totalMade = freeThrowsMade + threePointsMade + twoPointsMade

                if (totalMade <= 0) return null
                return freeThrowsMade + (threePointsMade * 3) + (twoPointsMade * 2)
            },
            getTeamStatsSnapshot(log) {
                const sources = [log, log.data, log.stats, log.event, log.payload].filter(
                    value => value && typeof value === 'object'
                )

                for (const source of sources) {
                    const team1Stats = source.team1 || source.homeTeam || source.home || null
                    const team2Stats = source.team2 || source.awayTeam || source.away || null
                    if (!team1Stats || !team2Stats) continue

                    const score1 = this.getTeamScoreFromStats(team1Stats)
                    const score2 = this.getTeamScoreFromStats(team2Stats)
                    if (score1 === null || score2 === null) continue

                    return { score1, score2 }
                }

                return null
            },
            getPoints(log) {
                const directPoints = this.toNumber(log.points ?? log.point ?? log.scoreValue ?? log.value)
                if (directPoints !== null) {
                    return Math.max(0, directPoints)
                }

                const madePoints = this.getMadePoints(log)
                if (madePoints !== null) {
                    return madePoints
                }

                return 0
            },
            getSnapshotScores(log) {
                const score1 = this.toNumber(log.scoreTeam1 ?? log.team1Score ?? log.homeScore)
                const score2 = this.toNumber(log.scoreTeam2 ?? log.team2Score ?? log.awayScore)
                if (score1 !== null && score2 !== null) {
                    return { score1, score2 }
                }

                const nested = log.score || log.currentScore
                if (nested && typeof nested === 'object') {
                    const n1 = this.toNumber(nested.scoreTeam1 ?? nested.team1Score ?? nested.homeScore)
                    const n2 = this.toNumber(nested.scoreTeam2 ?? nested.team2Score ?? nested.awayScore)
                    if (n1 !== null && n2 !== null) {
                        return { score1: n1, score2: n2 }
                    }
                }

                const scoreText = String(log.scoreText || log.score || this.extractText(log) || '')
                const scoreMatch = scoreText.match(/(\d{1,3})\s*[-:]\s*(\d{1,3})/)
                if (scoreMatch) {
                    return {
                        score1: Number(scoreMatch[1]),
                        score2: Number(scoreMatch[2])
                    }
                }

                return null
            },
            getQuarterMarkers(maxMinute) {
                const markers = [10, 20, 30, 40].filter(m => m <= maxMinute)
                for (let ot = 45; ot <= maxMinute; ot += 5) {
                    markers.push(ot)
                }
                return markers
            },
            getPeriodSegments(maxMinute) {
                const markers = this.getQuarterMarkers(maxMinute)
                const segments = []
                let start = 0

                markers.forEach((end, index) => {
                    const label = end <= 40 ? `${index + 1}η` : `Παρ. ${index - 3}`
                    segments.push({ start, end, label })
                    start = end
                })

                if (segments.length === 0) {
                    segments.push({ start: 0, end: Math.max(1, maxMinute || 1), label: '1η' })
                }

                return segments
            },
            buildSeries() {
                const normalizedLogs = this.normalizeLogsPayload(this.logs)
                const minuteSnapshots = new Map()
                const minuteEvents = {}
                const sortedLogs = [...normalizedLogs].sort((a, b) => {
                    const ma = this.getMinute(a)
                    const mb = this.getMinute(b)
                    if (ma === null && mb === null) return 0
                    if (ma === null) return 1
                    if (mb === null) return -1
                    return ma - mb
                })
                const firstTimestampMs = sortedLogs.length ? this.getTimestampMs(sortedLogs[0]) : null

                let runningTeam1 = 0
                let runningTeam2 = 0
                let hasAnySnapshot = false
                let lastLogMinute = 0

                sortedLogs.forEach((log) => {
                    const minute = this.getMinute(log, firstTimestampMs)
                    if (minute === null) return
                    lastLogMinute = Math.max(lastLogMinute, minute)

                    const teamStatsSnapshot = this.getTeamStatsSnapshot(log)
                    if (teamStatsSnapshot) {
                        hasAnySnapshot = true
                        runningTeam1 = teamStatsSnapshot.score1
                        runningTeam2 = teamStatsSnapshot.score2
                        minuteSnapshots.set(minute, { score1: runningTeam1, score2: runningTeam2 })
                        const eventText = this.extractText(log)
                        if (eventText) {
                            minuteEvents[minute] = [...(minuteEvents[minute] || []), eventText]
                        }
                        return
                    }

                    const snapshot = this.getSnapshotScores(log)
                    if (snapshot) {
                        hasAnySnapshot = true
                        runningTeam1 = snapshot.score1
                        runningTeam2 = snapshot.score2
                        minuteSnapshots.set(minute, { score1: runningTeam1, score2: runningTeam2 })
                        const eventText = this.extractText(log)
                        if (eventText) {
                            minuteEvents[minute] = [...(minuteEvents[minute] || []), eventText]
                        }
                        return
                    }

                    const points = this.getPoints(log)
                    if (points <= 0) return

                    const teamId = this.getTeamId(log)
                    const teamName = this.getTeamName(log)
                    const matchesTeam1Name = this.team1?.name && teamName.includes(this.team1.name.toLowerCase())
                    const matchesTeam2Name = this.team2?.name && teamName.includes(this.team2.name.toLowerCase())

                    if (teamId === this.team1?.id || matchesTeam1Name) {
                        runningTeam1 += points
                    } else if (teamId === this.team2?.id || matchesTeam2Name) {
                        runningTeam2 += points
                    } else {
                        return
                    }

                    minuteSnapshots.set(minute, { score1: runningTeam1, score2: runningTeam2 })
                    const eventText = this.extractText(log)
                    if (eventText && String(eventText).trim()) {
                        minuteEvents[minute] = [...(minuteEvents[minute] || []), eventText]
                    }
                })

                if (!hasAnySnapshot && minuteSnapshots.size === 0) {
                    return {
                        labels: [0],
                        team1Series: [0],
                        team2Series: [0],
                        minuteEvents: {},
                        maxMinute: 0,
                        derivedFrom: 'empty'
                    }
                }

                const maxMinuteInLogs = minuteSnapshots.size ? Math.max(...Array.from(minuteSnapshots.keys())) : 40
                const maxMinute = Math.max(40, maxMinuteInLogs)
                const labels = Array.from({ length: maxMinute + 1 }, (_, i) => i)

                const team1Series = []
                const team2Series = []
                let current1 = 0
                let current2 = 0

                // Minutes after the latest log haven't been played yet: leave them empty so the lines stop there
                const lastPlayedMinute = Math.max(lastLogMinute, maxMinuteInLogs)

                labels.forEach(minute => {
                    if (minute > lastPlayedMinute) {
                        team1Series.push(null)
                        team2Series.push(null)
                        return
                    }
                    const minuteValue = minuteSnapshots.get(minute)
                    if (minuteValue) {
                        current1 = minuteValue.score1
                        current2 = minuteValue.score2
                    }
                    team1Series.push(current1)
                    team2Series.push(current2)
                })

                return { labels, team1Series, team2Series, minuteEvents, maxMinute, derivedFrom: 'logs' }
            }
        }
    }
</script>

<style scoped>
.score-evolution-wrap {
    width: 100%;
    margin-top: 10px;
}

.score-evolution-shell {
    position: relative;
    overflow: visible;
    background: #fff;
}

.score-evolution-chart {
    position: relative;
    width: 100%;
    padding: 4px 4px 8px;
    min-height: 320px;
    height: 48vh;
    max-height: 520px;
}

/* ---------- Summary tiles (match page) ---------- */
.flow-summary {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 10px;
    margin-bottom: 14px;
    font-family: Ubuntu, Arial, sans-serif;
}

.flow-tile {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    padding: 10px 8px;
    border-radius: 10px;
    background: #f7f7f8;
    border-top: 3px solid #ff7133;
    text-align: center;
}

.flow-tile__team {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    max-width: 100%;
    overflow: hidden;
    color: rgba(0, 0, 0, 0.7);
    font-size: 0.78rem;
    font-weight: 700;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.flow-tile__team img {
    width: 22px;
    height: 22px;
    object-fit: contain;
}

.flow-tile__value {
    color: #111;
    font-size: 1.6rem;
    font-weight: 800;
    line-height: 1.1;
    font-variant-numeric: tabular-nums;
}

.flow-tile__label {
    color: rgba(0, 0, 0, 0.5);
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

/* ---------- Difference strip ---------- */
.flow-diff {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    font-family: Ubuntu, Arial, sans-serif;
}

.flow-diff__head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 6px 16px;
    margin-bottom: 6px;
}

.flow-diff__title {
    font-size: 0.85rem;
    font-weight: 800;
}

.flow-diff__legend {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
    color: rgba(0, 0, 0, 0.6);
    font-size: 0.75rem;
    font-weight: 600;
}

.flow-diff__plot {
    position: relative;
}

.flow-diff__hits {
    position: absolute;
    top: 0;
    height: 110px;
}

.flow-diff__svg {
    display: block;
    width: 100%;
    height: 110px;
    border-radius: 8px;
    background: #fafafb;
    overflow: visible;
}

.flow-diff__axis {
    stroke: rgba(0, 0, 0, 0.25);
    stroke-width: 1;
    vector-effect: non-scaling-stroke;
}

.flow-diff__marker {
    stroke: rgba(0, 0, 0, 0.15);
    stroke-width: 1;
    stroke-dasharray: 3 3;
    vector-effect: non-scaling-stroke;
}

.flow-diff__max {
    position: absolute;
    width: 32px;
    text-align: right;
    color: rgba(0, 0, 0, 0.45);
    font-size: 0.7rem;
    font-weight: 700;
}

.flow-diff__max--top {
    top: 0;
}

.flow-diff__max--bottom {
    top: 94px;
}

.flow-diff__periods {
    display: flex;
    margin-top: 4px;
    color: rgba(0, 0, 0, 0.5);
    font-size: 0.72rem;
    font-weight: 700;
}

.flow-diff__periods span {
    flex-basis: 0;
    text-align: center;
}

/* Broadcast: sized for a TV screen */
.score-evolution-broadcast {
    margin-top: 0;
}

.score-evolution-broadcast .score-evolution-shell {
    box-shadow: none;
}

.score-evolution-broadcast .flow-summary {
    gap: 14px;
    margin-bottom: 18px;
}

.score-evolution-broadcast .flow-tile {
    padding: 14px 10px;
    border-top-width: 4px;
}

.score-evolution-broadcast .flow-tile__team {
    font-size: 1.05rem;
}

.score-evolution-broadcast .flow-tile__team img {
    width: 30px;
    height: 30px;
}

.score-evolution-broadcast .flow-tile__value {
    font-size: 2.3rem;
}

.score-evolution-broadcast .flow-tile__label {
    font-size: 0.9rem;
}

.score-evolution-broadcast .score-evolution-chart {
    padding: 4px 10px 8px;
}

@media only screen and (max-width: 1000px) {
    .score-evolution-chart {
        min-height: 240px;
        height: 42vh;
        max-height: 360px;
    }

    /* phone: keep the chart's proportions instead of a share of the (tall) screen */
    .score-evolution-wrap:not(.score-evolution-broadcast) .score-evolution-chart {
        height: auto;
        min-height: 0;
        max-height: none;
        aspect-ratio: 4 / 3;
    }

    .flow-diff__svg,
    .flow-diff__hits {
        height: 90px;
    }

    .flow-diff__max--bottom {
        top: 74px;
    }

    .flow-summary {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

.score-legend {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    gap: 18px;
    margin-bottom: 4px;
    color: #2f3f53;
    font-family: Ubuntu, Arial, sans-serif;
    font-size: 0.85rem;
    font-weight: 700;
}

.legend-item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.legend-line {
    display: inline-block;
    width: 18px;
    height: 4px;
    border-radius: 2px;
}

.chart-note {
    text-align: center;
    margin-top: 6px;
    color: #a5b3c7;
    font-size: 12px;
}
</style>
